import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { applyMiddleware, createStore } from 'redux';
import { composeWithDevTools } from 'redux-devtools-extension/developmentOnly';
import thunk from 'redux-thunk';
import app, { createReducer } from './reducers';
import LiveEditing from './Components/LiveEditing';
import DynamicComponent from './Components/DynamicComponent';
import MiniCart from './Components/MiniCart';
import { flashMessagesLoaded } from './Actions/FlashMessages.action';

window.__litium = window.__litium || {};
const preloadState = window.__litium.preloadState || {};
const store = createStore(
    app,
    preloadState,
    composeWithDevTools(applyMiddleware(thunk))
);

// Add a dictionary to keep track of the registered async reducers
store.asyncReducers = {};

// Create an inject reducer function
// This function adds the async reducer, and creates a new combined reducer
store.injectReducer = (key, asyncReducer) => {
    if (!store.asyncReducers[key]) {
        store.asyncReducers[key] = asyncReducer;
        store.replaceReducer(createReducer(store.asyncReducers));
    }
};

window.__litium = {
    ...window.__litium,
    bootstrapComponents: () => {
        // bootstrap React components, in case the HTML response we receive from the server
        // has React components. ReactDOM.render performs only an update on previous rendered
        // components and only mutate the DOM as necessary to reflect latest React element.
        bootstrapComponents();
    },
    cache: {}, // for storing cache data for current request
};

const registeredContainers = [];
const renderReact = (element, container, callback) => {
    registeredContainers.push(container);
    ReactDOM.render(element, container, callback);
};

window.onunload = () => {
    // make sure components are unmounted, redux's listener are unsubscribed
    // to fix issue with iframe navigation in IE
    registeredContainers.forEach((container) => {
        ReactDOM.unmountComponentAtNode(container);
    });
};

const bootstrapComponents = () => {
    if (document.getElementById('liveEditing')) {
        renderReact(
            <Provider store={store}>
                <LiveEditing />
            </Provider>,
            document.getElementById('liveEditing')
        );
    }

    if (document.getElementById('miniCart')) {
        renderReact(
            <Provider store={store}>
                <MiniCart />
            </Provider>,
            document.getElementById('miniCart')
        );
    }

    if (document.getElementById('checkout')) {
        const Checkout = DynamicComponent({
            loader: () => import('./Components/Checkout'),
        });
        renderReact(
            <Provider store={store}>
                <Checkout />
            </Provider>,
            document.getElementById('checkout')
        );
    }

    if (document.querySelectorAll('buy-button').length > 0) {
        const BuyButton = DynamicComponent({
            loader: () => import('./Components/BuyButton'),
        });
        Array.from(document.querySelectorAll('buy-button')).forEach(
            (button) => {
                const {
                    label,
                    articleNumber,
                    isProductCardLayout,
                } = button.dataset;
                renderReact(
                    <Provider store={store}>
                        <BuyButton
                            {...{
                                label,
                                articleNumber,
                                isProductCardLayout,
                            }}
                        />
                    </Provider>,
                    button
                );
            }
        );
    }

    if (document.getElementById('newsletterSignUpModal')) {
        const NewsletterSignUpModal = DynamicComponent({
            loader: () => import('./Components/NewsletterSignUpModal'),
        });
        renderReact(
            <Provider store={store}>
                <NewsletterSignUpModal />
            </Provider>,
            document.getElementById('newsletterSignUpModal')
        );
    }

    if (document.querySelectorAll('newsletter-sign-up-block-component').length > 0) {
        const NewsletterSignUpBlock = DynamicComponent({
            loader: () => import('./Components/NewsletterSignUpBlock'),
        });
        Array.from(document.querySelectorAll('newsletter-sign-up-block-component')).forEach(
            (newsletterSignUpBlock) => {
                const {
                    successMessage,
                } = newsletterSignUpBlock.dataset;
                renderReact(
                    <Provider store={store}>
                        <NewsletterSignUpBlock
                            {...{
                                successMessage,
                            }}
                        />
                    </Provider>,
                    newsletterSignUpBlock
                );
            }
        );
    }

    if (document.getElementById('flashMessagesContainer')) {
        const FlashMessagesContainer = DynamicComponent({
            loader: () => import('./Components/FlashMessagesContainer')
        });
        store.dispatch(flashMessagesLoaded(preloadState?.flashMessages))
        renderReact(
            <Provider store={store}>
                <FlashMessagesContainer />
            </Provider>,
            document.getElementById('flashMessagesContainer')
        );
    }

    if (document.getElementById('softwareLicenseDirectDepositModal')) {
        const SoftwareLicenseDirectDepositModal = DynamicComponent({
            loader: () => import('./Components/SoftwareLicenseDirectDepositModal'),
        });
        const element = document.getElementById('softwareLicenseDirectDepositModal');
        const {
            modalId,
        } = element.dataset;
        renderReact(
            <Provider store={store}>
                <SoftwareLicenseDirectDepositModal
                    {...{
                        modalId
                    }}    
                />
            </Provider>,
            element
        );
    }

    if (document.querySelectorAll('software-license-direct-deposit-modal-trigger-component').length > 0) {
        const SoftwareLicenseDirectDepositModalTrigger = DynamicComponent({
            loader: () => import('./Components/SoftwareLicenseDirectDepositModalTrigger'),
        });
        Array.from(document.querySelectorAll('software-license-direct-deposit-modal-trigger-component')).forEach(
            (softwareLicenseDirectDepositModalTrigger) => {
                const {
                    modalId,
                    iconCssClass
                } = softwareLicenseDirectDepositModalTrigger.dataset;
                renderReact(
                    <Provider store={store}>
                        <SoftwareLicenseDirectDepositModalTrigger
                            {...{
                                modalId,
                                iconCssClass
                            }}
                        />
                    </Provider>,
                    softwareLicenseDirectDepositModalTrigger
                );
            }
        );
    }

    if (document.getElementById('openSoftubeCentral')) {
        const OpenSoftubeCentral = DynamicComponent({
            loader: () => import('./Components/OpenSoftubeCentral'),
        });
        const element = document.getElementById('openSoftubeCentral');
        const {
            downloadMacUrl,
            downloadWindowsUrl
        } = element.dataset;
        renderReact(
            <Provider store={store}>
                <OpenSoftubeCentral 
                    {...{
                        downloadMacUrl,
                        downloadWindowsUrl
                    }} 
                />
            </Provider>,
            element
        );
    }

    if (document.querySelectorAll('flow-credits-purchase-modal-component').length > 0) {
        const FlowCreditsPurchaseModal = DynamicComponent({
            loader: () => import('./Components/FlowCreditsPurchaseModal'),
        });
        Array.from(document.querySelectorAll('flow-credits-purchase-modal-component')).forEach(
            (flowCreditsPurchaseModal) => {
                const {
                    isUpgrade
                } = flowCreditsPurchaseModal.dataset;
                renderReact(
                    <Provider store={store}>
                        <FlowCreditsPurchaseModal
                            {...{
                                isUpgrade
                            }}
                        />
                    </Provider>,
                    flowCreditsPurchaseModal
                );
            }
        );
    }

    if (document.getElementById('myAccountUserInfo')) {
        const MyAccountUserInfo = DynamicComponent({
            loader: () => import ('./Components/MyAccount.UserInfo'),
        });
        renderReact(
            <Provider store={store}>
                <MyAccountUserInfo />
            </Provider>,
            document.getElementById('myAccountUserInfo')
        );
    }

    if (document.getElementById('myAccountChangeEmail')) {
        const MyAccountChangeEmail = DynamicComponent({
            loader: () => import ('./Components/MyAccount.ChangeEmail'),
        });
        renderReact(
            <Provider store={store}>
                <MyAccountChangeEmail />
            </Provider>,
            document.getElementById('myAccountChangeEmail')
        );
    }

    if (document.getElementById('myAccountChangePassword')) {
        const MyAccountChangePassword = DynamicComponent({
            loader: () => import ('./Components/MyAccount.ChangePassword'),
        });
        renderReact(
            <Provider store={store}>
                <MyAccountChangePassword />
            </Provider>,
            document.getElementById('myAccountChangePassword')
        );
    }
    
    if (document.querySelectorAll('tooltip-component').length > 0) {
        const Tooltip = DynamicComponent({
            loader: () => import('./Components/Tooltip'),
        });
        Array.from(document.querySelectorAll('tooltip-component')).forEach(
            (tooltip) => {
                const {
                    text
                } = tooltip.dataset;
                renderReact(
                    <Provider store={store}>
                        <Tooltip
                            {...{
                                text
                            }}
                        />
                    </Provider>,
                    tooltip
                );
            }
        );
    }

    if (document.getElementById('registerLicenseCode')) {
        const RegisterLicenseCode = DynamicComponent({
            loader: () => import('./Components/RegisterLicenseCode')
        });
        renderReact(
            <Provider store={store}>
                <RegisterLicenseCode />
            </Provider>,
            document.getElementById('registerLicenseCode')
        );
    }

    if (document.getElementById('linkIlok')) {
        const LinkIlok = DynamicComponent({
            loader: () => import('./Components/LinkIlok'),
        });
        renderReact(
            <Provider store={store}>
                <LinkIlok />
            </Provider>,
            document.getElementById('linkIlok')
        );
    }

    if (document.getElementById('myAccountMySubscriptions')) {
        const MyAccountMySubscriptions = DynamicComponent({
            loader: () => import ('./Components/MyAccountMySubscriptions'),
        });
        renderReact(
            <Provider store={store}>
                <MyAccountMySubscriptions />
            </Provider>,
            document.getElementById('myAccountMySubscriptions')
        );
    }
};

bootstrapComponents();
